import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";

interface VTBUpNextProps {
  videos: any[];
  selectedVideo: any | null;
  thumbnailUrl?: string;
  onVideoSelect: (video: any) => void;
}

// Helper to get a stable unique id for a video, regardless of which id field it uses
const getVideoId = (vid: any): string | undefined =>
  vid?.video_id || vid?.article_id;

const VTBUpNext: React.FC<VTBUpNextProps> = ({
  videos,
  selectedVideo,
  thumbnailUrl,
  onVideoSelect,
}) => {
  console.log("videos", videos);

  // Most-recently-played stack of video ids (most recent first).
  // Does NOT include the currently playing video.
  const [playHistory, setPlayHistory] = useState<string[]>([]);
  const prevSelectedIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentId = getVideoId(selectedVideo);
    const prevId = prevSelectedIdRef.current;

    // Only push to history when the selection actually changed
    // (and there was a previously playing video to push).
    if (currentId && prevId && prevId !== currentId) {
      setPlayHistory((prev) => {
        // Remove the newly selected video from history (it's now "current", not "next")
        // and remove the previous video if it already existed in history (avoid duplicates),
        // then place the previous video at the front (top of Watch Next).
        const filtered = prev.filter((id) => id !== currentId && id !== prevId);
        return [prevId, ...filtered];
      });
    }

    prevSelectedIdRef.current = currentId;
  }, [selectedVideo]);

  const currentId = getVideoId(selectedVideo);

  // Build a lookup so we can resolve history ids back to full video objects
  const videoMap = new Map(videos?.map((v: any) => [getVideoId(v), v]) || []);

  // 1) Previously played videos, most recent first (per playHistory order)
  const historyVideos = playHistory
    .map((id) => videoMap.get(id))
    .filter(Boolean);

  // 2) Remaining videos from the original list, excluding current + anything already in history,
  //    preserving original listing order
  const remainingVideos = (videos || []).filter((v: any) => {
    const id = getVideoId(v);
    return id !== currentId && !playHistory.includes(id as string);
  });

  const displayVideos = [...historyVideos, ...remainingVideos];
  return (
    <Box>
      <Typography
        variant="h6"
        color="primary"
        sx={{
          fontWeight: "bold",
          mb: 2,
          borderBottom: "2px solid #0f4c81",
          display: "inline-block",
          pb: 0.5,
        }}
      >
        Up Next in Library
      </Typography>

      <List
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          maxHeight: "400px",
          overflow: "auto",

          "&::-webkit-scrollbar": {
            width: "6px",
          },

          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
            borderRadius: "4px",
          },

          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
          },

          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#555",
          },
        }}
      >
        {/* {videos?.map((vid: any, index: number) => { */}
        {displayVideos.map((vid: any, index: number) => {
          const isSelected =
            selectedVideo?.video_id === vid?.video_id ||
            selectedVideo?.article_id === vid?.article_id;

          // NCG API does not provide thumbnailUrl inside every video document.
          // Therefore use the thumbnail received from getNCGThumnailDetails().
          const imageUrl = vid?.video_id
            ? `https://jgatenext.com/cdn/ncgvideotn/thumbnails/${vid.video_id}.jpg`
            : undefined;

          return (
            <ListItem
              key={vid?.article_id || vid?.video_id || index}
              disablePadding
              sx={{ mb: 1 }}
            >
              <ListItemButton
                alignItems="flex-start"
                onClick={() => onVideoSelect(vid)}
                sx={{
                  borderRadius: 1,

                  bgcolor: isSelected ? "#eaf3ff" : "transparent",

                  border: isSelected
                    ? "1px solid #0f4c81"
                    : "1px solid transparent",

                  "&:hover": {
                    bgcolor: isSelected ? "#eaf3ff" : "#f0f7ff",
                  },
                }}
              >
                <ListItemAvatar sx={{ mr: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={imageUrl}
                    alt={vid?.title || "Video thumbnail"}
                    sx={{
                      width: 100,
                      height: 56,
                      bgcolor: "#e9eef5",
                      flexShrink: 0,
                    }}
                  >
                    {!imageUrl && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "10px",
                          color: "text.secondary",
                        }}
                      >
                        Video
                      </Typography>
                    )}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        lineHeight: 1.2,
                        color: isSelected ? "primary.main" : "text.primary",

                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {vid?.title || "Untitled Video"}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      {vid?.videoId || ""}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default VTBUpNext;
